import type { Locale } from "@tutor/protocol";

// Strings for the sign-in and set-up screens. Parents read the set-up flow (calm,
// clear, "vous" in French). Children read the welcome, profile and PIN screens, so that
// copy stays short, warm and never blames ("tu" in French). "Tutor" is the product,
// named on parent screens only; "Coach" is the persona the child talks to and the only
// name a child sees. The child's PIN is "PIN" in English and
// "code" on child screens and "code secret" on parent screens in French, where the 6-digit
// e-mail code is also visible (docs/copy/README.md glossary). Placeholders are `{name}`. The AI wording (addChild.ai*, reconsent.*) is the
// approved transparency disclosure: see docs/copy/disclosure.md before changing it.

const en = {
  "common.loading": "Getting things ready…",
  "common.back": "Back",
  "common.cancel": "Cancel",
  "common.continue": "Continue",
  "common.retry": "Try again",
  "common.offline": "You're offline. Check the Wi-Fi and try again.",
  "common.rateLimited":
    "Lots of tries in a row! Take a little break and try again in a few minutes.",
  "common.error": "Something went wrong. Please try again.",
  "common.switchLanguage": "Français",
  "common.switchLanguageLabel": "Passer en français",
  "common.unavailable": "We can't reach Coach right now",

  "welcome.title": "Hello! Let's get this device ready",
  "welcome.body":
    "A grown-up sets up this device once. After that, you just tap your name and type your PIN.",
  "welcome.parentHere": "A parent is here",
  "welcome.parentHereHint": "Sign in with your email to set up this device.",
  "welcome.pairing": "Use a pairing code",
  "welcome.pairingHint": "A parent made a code in their own app.",
  "welcome.deviceRemoved": "This device was disconnected. Ask a parent to set it up again.",
  "welcome.noProfiles": "Nobody can sign in on this device yet. Ask a parent to set it up again.",

  "parent.emailTitle": "Parent sign-in",
  "parent.emailBody": "We'll email you a 6-digit code. Children never need an email address.",
  "parent.emailLabel": "Your email address",
  "parent.sendCode": "Send me a code",
  "parent.invalidEmail": "Please enter a valid email address.",
  "parent.sessionExpired": "For safety, please sign in again with a new code.",
  "parent.codeTitle": "Check your email",
  "parent.codeBody": "Type the 6-digit code we sent to {email}.",
  "parent.codeLabel": "6-digit code",
  "parent.signIn": "Sign in",
  "parent.wrongCode": "That code didn't work. Check it and try again.",
  "parent.codeExpired": "That code has expired or was tried too many times. Ask for a new one.",
  "parent.newCode": "Send a new code",
  "parent.codeSent": "We sent a new code.",

  "invite.title": "Your invitation",
  "invite.body": "The alpha is invitation-only. Type the invitation code you received.",
  "invite.label": "Invitation code",
  "invite.submit": "Use this code",
  "invite.invalid": "That invitation code didn't work. Check it and try again.",
  "invite.emailMismatch":
    "This invitation was sent to a different email address. Sign in with the address that received it.",

  "children.title": "Who will learn on this device?",
  "children.body": "Choose the children who will use this device.",
  "children.add": "Add a child",
  "children.noneSelected": "Choose at least one child.",

  "addChild.title": "Add a child",
  "addChild.body": "Only your family sees this name. Coach never does.",
  "addChild.name": "First name or nickname",
  "addChild.birthYear": "Year of birth",
  "addChild.language": "Language for learning",
  "addChild.english": "English",
  "addChild.french": "Français",
  "addChild.aiTitle": "Before you start: how Tutor works",
  "addChild.aiBody":
    "Your child practises with Coach, a computer program that uses AI (Anthropic's Claude models, under zero data retention) to write its questions and hints. Coach never receives your child's name, birth year or your email. Progress, the next exercise and the level of help are decided by our own learning engine, not by the AI. Coach can make mistakes, and it never replaces you or a teacher.",
  "addChild.noticeLink": "Read the privacy notice",
  "addChild.safetyNote":
    "If your child writes something suggesting they may be in danger, we notify you; this cannot be switched off.",
  "addChild.consent":
    "I am this child's parent or legal guardian. I have read the privacy notice (version {version}) and I agree to my child using Tutor, including Coach, which uses AI to write its messages.",
  "addChild.consentRequired": "Please accept the privacy notice to continue.",
  "addChild.invalid": "Please check the name and year of birth.",
  "addChild.submit": "Add this child",

  "reconsent.title": "The privacy notice has changed",
  "reconsent.body":
    "We have updated the privacy notice (version {version}). Please read it and confirm your consent before your child continues.",
  "reconsent.accept": "I have read it and I agree",
  "reconsent.later": "Not now",

  "pin.setTitle": "Choose a PIN for {name}",
  "pin.setBody": "4 to 6 digits. {name} types it to sign in on this device.",
  "pin.label": "PIN",
  "pin.confirmLabel": "Type the PIN again",
  "pin.invalid": "The PIN needs 4 to 6 digits.",
  "pin.mismatch": "The two PINs don't match.",
  "pin.sameAsParentCode":
    "This is a parent code on one of this child's devices. Choose another PIN.",
  "pin.save": "Save PIN",
  "pin.skip": "Keep the current PIN",

  "enroll.title": "Last step",
  "enroll.body":
    "Give this device a name so you can recognise it later. When you finish, you'll be signed out on this device.",
  "enroll.label": "Device name",
  "enroll.defaultLabel": "Learning tablet",
  "enroll.submit": "Finish set-up",

  "pairing.title": "Use a pairing code",
  "pairing.body": "Type the code shown in your parent's app.",
  "pairing.label": "Pairing code",
  "pairing.submit": "Connect this device",
  "pairing.invalid": "That code didn't work. Check it, or ask a parent for a new one.",

  "profiles.title": "Who's learning today?",
  "profiles.pick": "Sign in as {name}",
  "profiles.noPin": "Ask a parent to choose your PIN",

  "pad.title": "Hi {name}!",
  "pad.body": "Type your PIN",
  "pad.delete": "Delete last digit",
  "pad.submit": "Go",
  "pad.entered": "{count} of up to 6 digits typed",
  "pad.notYou": "Not you?",
  "pad.wrong": "Hmm, that's not quite it. Have another go!",
  "pad.locked": "Your PIN is locked for now to keep your account safe. Ask a parent to help.",
  "pad.notSet": "You don't have a PIN yet. Ask a parent to choose one.",
} as const;

export type AuthStringKey = keyof typeof en;
type Strings = Readonly<Record<AuthStringKey, string>>;

const fr: Strings = {
  "common.loading": "Préparation en cours…",
  "common.back": "Retour",
  "common.cancel": "Annuler",
  "common.continue": "Continuer",
  "common.retry": "Réessayer",
  "common.offline": "Pas de connexion. Vérifie le Wi-Fi et réessaie.",
  "common.rateLimited":
    "Beaucoup d'essais d'affilée ! Fais une petite pause et réessaie dans quelques minutes.",
  "common.error": "Un problème est survenu. Merci de réessayer.",
  "common.switchLanguage": "English",
  "common.switchLanguageLabel": "Switch to English",
  "common.unavailable": "Impossible de joindre Coach pour l'instant",

  "welcome.title": "Bonjour ! Préparons cet appareil",
  "welcome.body":
    "Un adulte prépare cet appareil une seule fois. Ensuite, tu touches ton prénom et tu tapes ton code.",
  "welcome.parentHere": "Un parent est là",
  "welcome.parentHereHint": "Connectez-vous avec votre e-mail pour préparer cet appareil.",
  "welcome.pairing": "Utiliser un code d'appairage",
  "welcome.pairingHint": "Un parent a créé un code dans son application.",
  "welcome.deviceRemoved":
    "Cet appareil a été déconnecté. Demande à un parent de le préparer à nouveau.",
  "welcome.noProfiles":
    "Personne ne peut encore se connecter sur cet appareil. Demande à un parent de le préparer à nouveau.",

  "parent.emailTitle": "Connexion parent",
  "parent.emailBody":
    "Nous vous envoyons un code à 6 chiffres par e-mail. Les enfants n'ont jamais besoin d'adresse e-mail.",
  "parent.emailLabel": "Votre adresse e-mail",
  "parent.sendCode": "M'envoyer un code",
  "parent.invalidEmail": "Merci de saisir une adresse e-mail valide.",
  "parent.sessionExpired": "Par sécurité, reconnectez-vous avec un nouveau code.",
  "parent.codeTitle": "Consultez vos e-mails",
  "parent.codeBody": "Saisissez le code à 6 chiffres envoyé à {email}.",
  "parent.codeLabel": "Code à 6 chiffres",
  "parent.signIn": "Se connecter",
  "parent.wrongCode": "Ce code ne fonctionne pas. Vérifiez-le et réessayez.",
  "parent.codeExpired": "Ce code a expiré ou a été essayé trop de fois. Demandez-en un nouveau.",
  "parent.newCode": "Envoyer un nouveau code",
  "parent.codeSent": "Nous vous avons envoyé un nouveau code.",

  "invite.title": "Votre invitation",
  "invite.body": "L'alpha est sur invitation. Saisissez le code d'invitation que vous avez reçu.",
  "invite.label": "Code d'invitation",
  "invite.submit": "Utiliser ce code",
  "invite.invalid": "Ce code d'invitation ne fonctionne pas. Vérifiez-le et réessayez.",
  "invite.emailMismatch":
    "Cette invitation a été envoyée à une autre adresse e-mail. Connectez-vous avec l'adresse qui l'a reçue.",

  "children.title": "Qui va apprendre sur cet appareil ?",
  "children.body": "Choisissez les enfants qui utiliseront cet appareil.",
  "children.add": "Ajouter un enfant",
  "children.noneSelected": "Choisissez au moins un enfant.",

  "addChild.title": "Ajouter un enfant",
  "addChild.body": "Seule votre famille voit ce prénom. Coach ne le voit jamais.",
  "addChild.name": "Prénom ou surnom",
  "addChild.birthYear": "Année de naissance",
  "addChild.language": "Langue d'apprentissage",
  "addChild.english": "English",
  "addChild.french": "Français",
  "addChild.aiTitle": "Avant de commencer : comment Tutor fonctionne",
  "addChild.aiBody":
    "Votre enfant s'entraîne avec Coach, un programme informatique qui utilise une IA (les modèles Claude d'Anthropic, sans aucune conservation des données) pour rédiger ses questions et ses indices. Coach ne reçoit jamais le prénom ni l'année de naissance de votre enfant, ni votre e-mail. La progression, l'exercice suivant et le niveau d'aide sont décidés par notre propre moteur d'apprentissage, pas par l'IA. Coach peut se tromper, et il ne remplace ni vous ni un enseignant.",
  "addChild.noticeLink": "Lire la notice de confidentialité",
  "addChild.safetyNote":
    "Si votre enfant écrit quelque chose qui laisse penser qu'il est en danger, vous êtes prévenu ; cela ne peut pas être désactivé.",
  "addChild.consent":
    "Je suis le parent ou le représentant légal de cet enfant. J'ai lu la notice de confidentialité (version {version}) et j'accepte que mon enfant utilise Tutor, y compris Coach, qui s'appuie sur une IA pour rédiger ses messages.",
  "addChild.consentRequired": "Merci d'accepter la notice de confidentialité pour continuer.",
  "addChild.invalid": "Merci de vérifier le prénom et l'année de naissance.",
  "addChild.submit": "Ajouter cet enfant",

  "reconsent.title": "La notice de confidentialité a changé",
  "reconsent.body":
    "Nous avons mis à jour la notice de confidentialité (version {version}). Merci de la lire et de confirmer votre accord avant que votre enfant continue.",
  "reconsent.accept": "Je l'ai lue et j'accepte",
  "reconsent.later": "Plus tard",

  "pin.setTitle": "Choisissez un code secret pour {name}",
  "pin.setBody": "4 à 6 chiffres. {name} le tape pour se connecter sur cet appareil.",
  "pin.label": "Code secret",
  "pin.confirmLabel": "Retapez le code secret",
  "pin.invalid": "Le code secret doit comporter 4 à 6 chiffres.",
  "pin.mismatch": "Les deux codes secrets ne correspondent pas.",
  "pin.sameAsParentCode":
    "C'est un code parent sur un appareil de cet enfant. Choisissez un autre code secret.",
  "pin.save": "Enregistrer le code secret",
  "pin.skip": "Garder le code secret actuel",

  "enroll.title": "Dernière étape",
  "enroll.body":
    "Donnez un nom à cet appareil pour le reconnaître plus tard. À la fin, vous serez déconnecté de cet appareil.",
  "enroll.label": "Nom de l'appareil",
  "enroll.defaultLabel": "Tablette d'apprentissage",
  "enroll.submit": "Terminer",

  "pairing.title": "Utiliser un code d'appairage",
  "pairing.body": "Tape le code affiché dans l'application de ton parent.",
  "pairing.label": "Code d'appairage",
  "pairing.submit": "Connecter cet appareil",
  "pairing.invalid":
    "Ce code ne fonctionne pas. Vérifie-le ou demande un nouveau code à un parent.",

  "profiles.title": "Qui s'entraîne aujourd'hui ?",
  "profiles.pick": "Se connecter : {name}",
  "profiles.noPin": "Demande à un parent de choisir ton code",

  "pad.title": "Salut {name} !",
  "pad.body": "Tape ton code",
  "pad.delete": "Effacer le dernier chiffre",
  "pad.submit": "C'est parti",
  "pad.entered": "{count} chiffres tapés sur 6 au maximum",
  "pad.notYou": "Ce n'est pas toi ?",
  "pad.wrong": "Hmm, ce n'est pas tout à fait ça. Essaie encore !",
  "pad.locked":
    "Ton code est bloqué pour le moment, pour protéger ton compte. Demande à un parent de t'aider.",
  "pad.notSet": "Tu n'as pas encore de code. Demande à un parent d'en choisir un.",
};

export const authStrings: Readonly<Record<Locale, Strings>> = { en, fr };

export type Translate = (key: AuthStringKey, vars?: Readonly<Record<string, string>>) => string;

export function translator(locale: Locale): Translate {
  const strings = authStrings[locale];
  return (key, vars = {}) =>
    strings[key].replace(/\{(\w+)\}/g, (match, name: string) => vars[name] ?? match);
}

/** The app's language before a child is picked: French if the browser prefers it. */
export function detectLocale(
  languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages,
): Locale {
  for (const language of languages) {
    const primary = language.toLowerCase().split("-")[0];
    if (primary === "fr" || primary === "en") return primary;
  }
  return "en";
}
